import { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { Card, CardContent } from "./ui/card";
import {
  Users,
  Search,
  Filter,
  Download,
  Eye,
  Loader2,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  UserCircle,
  Mail,
  IdCard,
  ShieldCheck,
  ShieldOff
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "motion/react";
import adminService, { AdminUser } from "../services/admin.service";
import { UserDetailsDialog } from "./UserDetailsDialog";
import { ConfirmationDialog } from "./ui/confirmation-dialog";
import { useAuth } from "../contexts/AuthContext";

interface AdminUserManagementProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AdminUserManagement({ open, onOpenChange }: AdminUserManagementProps) {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [roleChange, setRoleChange] = useState<{ open: boolean; user: AdminUser | null }>({
    open: false,
    user: null
  });
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [genderFilter, setGenderFilter] = useState('all');
  const [verificationFilter, setVerificationFilter] = useState('all');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const limit = 10;

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    verified: 0,
    unverified: 0,
    male: 0,
    female: 0,
    admins: 0,
    students: 0,
    recentRegistrations: 0
  });

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await adminService.getUsers({
        page: currentPage,
        limit,
        search: searchTerm,
        role: roleFilter === 'all' ? '' : roleFilter,
        gender: genderFilter === 'all' ? '' : genderFilter,
        isVerified: verificationFilter === 'all' ? '' : verificationFilter,
        sortBy: 'createdAt',
        sortOrder: 'desc'
      });

      setUsers(response.data.users);
      setTotalPages(response.data.pagination.pages);
      setTotalUsers(response.data.pagination.total);
    } catch (error: any) {
      toast.error(error.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, roleFilter, genderFilter, verificationFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await adminService.getUserStats();
      setStats(response.data);
    } catch (error: any) {
      // Failed to fetch stats
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchUsers();
      fetchStats();
    }
  }, [open, fetchUsers, fetchStats]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filterType: 'role' | 'gender' | 'verification', value: string) => {
    setCurrentPage(1);
    switch (filterType) {
      case 'role':
        setRoleFilter(value);
        break;
      case 'gender':
        setGenderFilter(value);
        break;
      case 'verification':
        setVerificationFilter(value);
        break;
    }
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      await adminService.exportUsersToCSV({
        search: searchTerm,
        role: roleFilter === 'all' ? '' : roleFilter,
        gender: genderFilter === 'all' ? '' : genderFilter,
        isVerified: verificationFilter === 'all' ? '' : verificationFilter
      });
      toast.success('Users exported successfully');
    } catch (error: any) {
      toast.error(error.message || 'Failed to export users');
    } finally {
      setExporting(false);
    }
  };

  const handleViewDetails = (userId: string) => {
    setSelectedUserId(userId);
    setShowUserDetails(true);
  };

  const handleRoleChangeConfirm = async () => {
    const target = roleChange.user;
    if (!target) return;

    const nextRole = target.role === 'admin' ? 'student' : 'admin';
    try {
      await adminService.updateUserRole(target._id, nextRole);
      toast.success(
        nextRole === 'admin'
          ? `${target.name} is now an admin`
          : `${target.name} is no longer an admin`
      );
      fetchUsers();
      fetchStats();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update role');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setRoleFilter('all');
    setGenderFilter('all');
    setVerificationFilter('all');
    setCurrentPage(1);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="w-[95vw] max-w-[calc(100%-2rem)] sm:max-w-[1400px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-2xl">
              <Users className="w-6 h-6 text-[#d4af37]" />
              User Management
            </DialogTitle>
          </DialogHeader>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-[#0f1f3d]">{stats.total}</p>
                  <p className="text-sm text-gray-600">Total Users</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-green-600">{stats.verified}</p>
                  <p className="text-sm text-gray-600">Verified</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-red-600">{stats.unverified}</p>
                  <p className="text-sm text-gray-600">Unverified</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-blue-600">{stats.recentRegistrations}</p>
                  <p className="text-sm text-gray-600">Last 30 Days</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters and Search */}
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by name, email, or student ID..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                />
              </div>
              <Button
                onClick={handleExportCSV}
                disabled={exporting}
                className="btn-gold"
              >
                {exporting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Exporting...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Export CSV
                  </>
                )}
              </Button>
            </div>

            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600">Filters:</span>
              </div>
              
              <Select value={roleFilter} onValueChange={(value) => handleFilterChange('role', value)}>
                <SelectTrigger className="admin-filter">
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="student">Student</SelectItem>
                  <SelectItem value="parent">Parent/Guardian</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>

              <Select value={genderFilter} onValueChange={(value) => handleFilterChange('gender', value)}>
                <SelectTrigger className="admin-filter">
                  <SelectValue placeholder="All Genders" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Genders</SelectItem>
                  <SelectItem value="male">Male</SelectItem>
                  <SelectItem value="female">Female</SelectItem>
                </SelectContent>
              </Select>

              <Select value={verificationFilter} onValueChange={(value) => handleFilterChange('verification', value)}>
                <SelectTrigger className="admin-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="true">Verified</SelectItem>
                  <SelectItem value="false">Unverified</SelectItem>
                </SelectContent>
              </Select>

              {(searchTerm || roleFilter !== 'all' || genderFilter !== 'all' || verificationFilter !== 'all') && (
                <Button variant="outline" onClick={clearFilters}>
                  Clear Filters
                </Button>
              )}
            </div>
          </div>

          {/* Users Table */}
          <div className="border rounded-lg overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#d4af37]" />
              </div>
            ) : users.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="admin-table-cards w-full min-w-[900px]">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Student ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Gender
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Role
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Bookings
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {users.map((user) => (
                        <motion.tr
                          key={user._id}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="hover:bg-gray-50 transition-colors"
                        >
                          <td data-label="User" className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-3">
                              <UserCircle className="w-8 h-8 text-gray-400" />
                              <div>
                                <p className="font-medium text-gray-900">{user.name}</p>
                                <div className="flex items-center gap-1 text-sm text-gray-500">
                                  <Mail className="w-3 h-3" />
                                  {user.email}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td data-label="Student ID" className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1 text-sm text-gray-900">
                              <IdCard className="w-4 h-4 text-gray-400" />
                              {user.studentId || 'N/A'}
                            </div>
                          </td>
                          <td data-label="Gender" className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900 capitalize">{user.gender}</span>
                          </td>
                          <td data-label="Role" className="px-6 py-4 whitespace-nowrap">
                            <Badge
                              style={
                                user.role === 'admin'
                                  ? { backgroundColor: '#f3e8ff', color: '#6b21a8' }
                                  : user.role === 'parent'
                                  ? { backgroundColor: '#fef3c7', color: '#92400e' }
                                  : { backgroundColor: '#dbeafe', color: '#1e40af' }
                              }
                            >
                              {user.role === 'parent' ? 'PARENT/GUARDIAN' : user.role.toUpperCase()}
                            </Badge>
                          </td>
                          <td data-label="Status" className="px-6 py-4 whitespace-nowrap">
                            {user.isVerified ? (
                              <Badge className="bg-green-100 text-green-800">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Verified
                              </Badge>
                            ) : (
                              <Badge className="bg-red-100 text-red-800">
                                <XCircle className="w-3 h-3 mr-1" />
                                Unverified
                              </Badge>
                            )}
                          </td>
                          <td data-label="Bookings" className="px-6 py-4 whitespace-nowrap">
                            <span className="text-sm text-gray-900">{user.bookingCount}</span>
                          </td>
                          <td data-label="Actions" className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleViewDetails(user._id)}
                                className="text-[#0f1f3d] hover:bg-[#d4af37]/10"
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                              {user.email !== currentUser?.email && (
                                user.role === 'admin' ? (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setRoleChange({ open: true, user })}
                                    className="text-red-700 hover:bg-red-50"
                                  >
                                    <ShieldOff className="w-4 h-4 mr-1" />
                                    Remove admin
                                  </Button>
                                ) : (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setRoleChange({ open: true, user })}
                                    className="text-[#0f1f3d] hover:bg-[#d4af37]/10"
                                  >
                                    <ShieldCheck className="w-4 h-4 mr-1" />
                                    Make admin
                                  </Button>
                                )
                              )}
                            </div>
                          </td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t">
                  <div className="text-sm text-gray-700">
                    Showing <span className="font-medium">{(currentPage - 1) * limit + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(currentPage * limit, totalUsers)}</span> of{' '}
                    <span className="font-medium">{totalUsers}</span> users
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No users found</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* User Details Dialog */}
      <UserDetailsDialog
        open={showUserDetails}
        onOpenChange={setShowUserDetails}
        userId={selectedUserId}
      />

      {/* Role change confirmation */}
      <ConfirmationDialog
        open={roleChange.open}
        onOpenChange={(open) => setRoleChange((prev) => ({ open, user: open ? prev.user : null }))}
        title={roleChange.user?.role === 'admin' ? 'Remove Admin' : 'Make Admin'}
        description={
          roleChange.user?.role === 'admin'
            ? `Remove admin access from ${roleChange.user?.name}? They will become a regular student.`
            : `Grant ${roleChange.user?.name} full admin access to the system?`
        }
        confirmText={roleChange.user?.role === 'admin' ? 'Remove admin' : 'Make admin'}
        variant={roleChange.user?.role === 'admin' ? 'destructive' : 'default'}
        onConfirm={handleRoleChangeConfirm}
      />
    </>
  );
}
